<script setup lang="ts">
import { computed } from 'vue'
import GwVirtualList, {
  type GwVirtualListProps,
} from '@/components/lib/GwVirtualList/GwVirtualList.vue'
import GwTabs, { type GwTabsProps } from '@/components/lib/GwTabs/GwTabs.vue'
import GwDragDropper from '@/components/lib/GwDragDropper/GwDragDropper.vue'
import { type DragDropArgs } from '@/components/lib/GwDragDropper/GwDragDropperUtils'
import GwBtn from '@/components/lib/GwBtn/GwBtn.vue'
import GwBox from '@/components/lib/GwBox/GwBox.vue'
import { t } from '@/locales/i18n'
import type { GwTabType } from './GwTab.vue'
import GwTooltipNew from '../GwTooltip/GwTooltipNew.vue'

export interface GwTabsInfiniteProps {
  gwTabsProps?: Omit<GwTabsProps, 'options' | 'value'>
  gwVirtualListProps?: Omit<GwVirtualListProps<GwTabType>, 'items'>
  options: GwTabsProps['options']
  value: GwTabsProps['value']
  loadingVariant?: 'initial' | 'left' | 'right'
  isLoading?: boolean
}

const props = withDefaults(defineProps<GwTabsInfiniteProps>(), {
  gwTabsProps: () => ({
    hasActiveAutoScroll: true,
  }),
  gwVirtualListProps: () => ({
    direction: 'horizontal',
    idKey: 'id',
    hasDynamicSize: true,
    dynamicMinSize: 40,
    itemSize: 20,
    start: 0,
    offset: 0,
  }),
})

const emit = defineEmits([
  'scroll:update',
  'drag:drop',
  'click',
  'scrolled:end',
  'scrolled:start',
  'update:renderedItems',
  'create:new',
  'tab:close',
])

const handleScrollUpdate = (e: Event, visibleItems: Array<any>) => {
  emit('scroll:update', e, visibleItems)
}

const handleDrop = (e: Event, args: DragDropArgs<GwTabType>) => {
  emit('drag:drop', e, args)
}

const closeDisabled = computed(() => props.options.length === 1)
</script>

<template>
  <GwDragDropper
    dropTargetIdPart="tab_"
    direction="horizontal"
    @drag:drop="handleDrop"
  >
    <template #shallowitem="{ draggedData }">
      <GwBox
        v-if="draggedData"
        variant="box_dragDrop_item"
      >
        <span v-html="draggedData.name" />
      </GwBox>
    </template>
    <template #default="{ methods: dragDropperMethods, isDragging }">
      <GwVirtualList
        :items="options"
        v-bind="
          isDragging
            ? {
                ...$attrs,
                ...props.gwVirtualListProps, //
                gwScrollContProps: {
                  ...props.gwVirtualListProps.gwScrollContProps,
                  hasEdgeAutoScroll: true,
                  hasDragScroll: false,
                  disabled: loadingVariant === 'initial',
                },
              }
            : {
                ...$attrs,
                ...props.gwVirtualListProps,
                gwScrollContProps: {
                  ...props.gwVirtualListProps.gwScrollContProps,
                  hasDragScroll: true,
                  hasEdgeAutoScroll: false,
                  disabled: loadingVariant === 'initial',
                },
              }
        "
        @scroll:update="handleScrollUpdate"
        @scrolled:start="emit('scrolled:start')"
        @scrolled:end="emit('scrolled:end')"
        @update:renderedItems="count => emit('update:renderedItems', count)"
      >
        <template #default="{ visibleItems: items, scrollContStyle }">
          <slot />
          <GwTabs
            :style="scrollContStyle"
            :options="items"
            :value="props.value"
            v-bind="props.gwTabsProps"
            :disabled="isDragging"
            :hasDynamicSizeEmit="true"
            :loading="loadingVariant === 'initial'"
            @click="(...params) => emit('click', ...params)"
            @tab:mousedown="(event, item) => dragDropperMethods.startDelayedDrag(event, item)"
            @tab:touchstart="(event, item) => dragDropperMethods.startDelayedDrag(event, item)"
            @tab:mouseup="() => dragDropperMethods.endDelayedStartDrag()"
            @tab:touchend="() => dragDropperMethods.endDelayedStartDrag()"
          >
            <template #default="{ item }">
              <slot
                name="tabmenu"
                :item="item"
              />
              {{ item.name }}
              <GwTooltipNew
                :closeOnContentHover="true"
                placement="right"
                :tooltip="t('btn.close')"
                :disabled="closeDisabled"
              >
                <template #opener="{ listeners }">
                  <GwBtn
                    data-tst="tab_close"
                    icon="mdi-close"
                    :disabled="closeDisabled"
                    variant="btn_text_grey_error"
                    class="gw_hvr_content"
                    v-on="listeners"
                    @click.stop="emit('tab:close', item)"
                  ></GwBtn>
                </template>
              </GwTooltipNew>
            </template>
          </GwTabs>
        </template>
        <template #content_before>
          <slot name="content_before"></slot>
        </template>
        <template #content_after>
          <slot name="content_after"></slot>
        </template>
      </GwVirtualList>
    </template>
  </GwDragDropper>
</template>
